import { ref } from 'vue';
import { nestCall, NestApiType } from '@/composables/nestApi';

/**
 * Interface for a shop list item
 */
export interface ShopList {
  id_shop_list: number;
  name: string;
}

/**
 * Composable to fetch shop lists
 * Returns lists with valid = 1 and active = 1
 */
export function useShopLists() {
  const lists = ref<ShopList[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch all valid and active shop lists with pagination
   */
  const fetchLists = async (onlyListType = false) => {
    loading.value = true;
    error.value = null;

    const allLists: ShopList[] = [];
    let offset = 0;
    const limit = 50;
    let hasNextPage = true;

    try {
      while (hasNextPage) {
        const filters: any = [
          { field: 'valid', operator: 'EQUALS', values: 1 },
          { field: 'active', operator: 'EQUALS', values: 1 },
        ];
        if (onlyListType) {
          filters.push(
            { field: 'type', operator: 'EQUALS', values: 'list' },
          );
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/lists',
          {},
          '',
          undefined,
          true,
          {
            fields: 'id_shop_list,name',
            filters,
            order: [
              { field: 'date_modification', type: 'desc' },
            ],
            limit,
            offset,
          },
        );

        if (response && response.error) {
          console.error('API Error:', response.error);
          error.value = response.error;
          break;
        }

        if (!response || !response.data) {
          break;
        }

        const pageLists = response.data.map((item: { id_shop_list: number; name: string }) => ({
          id_shop_list: item.id_shop_list,
          name: item.name,
        }));
        allLists.push(...pageLists);

        // Check if there are more pages
        hasNextPage = response.meta?.pagination?.has_next_page ?? false;
        offset += limit;
      }

      lists.value = allLists;
    } catch (err) {
      console.error('Request failed:', err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
      lists.value = [];
    } finally {
      loading.value = false;
    }
  };

  return {
    lists,
    loading,
    error,
    fetchLists,
  };
}
